import { Box, Grid, Stack } from '@mui/material';
import Tag from './Tag';
import RichText from '../../../RichText';
import { SectionTitleType } from '../../../../utils/mappers/sectionTitleMapper';
import RichCtaButton from '../../../elements/RichCtaButton';
import getRichTextSummary from '../../../RichText/getRichTextSummary';
import OptionalViewport from '../../../OptionalViewport';
import Optional from '../../../Optional';

type Props = {
  data: SectionTitleType;
  isMobile: boolean;
};

const Title = ({ data, isMobile }: Props) => {
  const { title, styles, subtitle, tag, cta } = data;
  const { align, underline, buttonPlacement } = styles;
  const alignSelf =
    align === 'right' ? 'end' : align === 'left' ? 'start' : 'center';

  const ctaIsTop = !!cta && buttonPlacement === 'top';
  const isSubtitleRight = align === 'left' && !ctaIsTop && !!subtitle;
  const uSize = '60%';

  return (
    <Stack
      spacing={{ xs: 1.5, md: 2 }}
      component={'hgroup'}
      sx={{ width: '100%' }}
    >
      <Optional condition={!!tag}>
        <Box sx={{ alignSelf }}>
          <Tag>{tag}</Tag>
        </Box>
      </Optional>
      <Grid container spacing={2}>
        <OptionalViewport
          viewport={'d'}
          initIsMobile={isMobile}
          condition={ctaIsTop && align === 'right'}
        >
          <Grid size={'auto'} sx={{ alignSelf: 'end' }}>
            <RichCtaButton
              cta={cta}
              track={{ position: getRichTextSummary(title) }}
            />
          </Grid>
        </OptionalViewport>
        <Grid size={'grow'}>
          <Stack spacing={{ xs: 2, md: 3 }} sx={{ width: '100%' }}>
            <RichText
              withTypography
              variant={'h2'}
              component={'h2'}
              color={'textPrimary'}
              sx={{
                alignSelf: alignSelf,
                textAlign: align,
                color: 'text.primary',
                paddingBottom: 1,
                position: 'relative',
                '&::after': underline
                  ? {
                      content: '""',
                      position: 'absolute',
                      bottom: 0,
                      left:
                        underline === 'left'
                          ? 0
                          : underline === 'center'
                            ? uSize
                            : undefined,
                      right: underline === 'right' ? 0 : undefined,
                      transform:
                        underline === 'center'
                          ? `translateX(-${uSize})`
                          : undefined,
                      width: uSize,
                      height: '4px',
                      bgcolor: 'secondary.main',
                    }
                  : {},
              }}
            >
              {title}
            </RichText>
            <OptionalViewport viewport={'m'} initIsMobile={isMobile}>
              <RichText
                withTypography
                variant={'h6'}
                component={'p'}
                color={'textSecondary'}
                sx={{
                  alignSelf,
                }}
              >
                {subtitle}
              </RichText>
            </OptionalViewport>
            <OptionalViewport
              viewport={'d'}
              initIsMobile={isMobile}
              condition={!isSubtitleRight}
            >
              <RichText
                withTypography
                variant={'h6'}
                component={'p'}
                color={'textSecondary'}
                sx={{
                  alignSelf,
                }}
              >
                {subtitle}
              </RichText>
            </OptionalViewport>
            <OptionalViewport
              viewport={'d'}
              initIsMobile={isMobile}
              condition={ctaIsTop && align === 'center'}
            >
              <Box sx={{ alignSelf }}>
                <RichCtaButton
                  cta={cta}
                  track={{ position: getRichTextSummary(title) }}
                />
              </Box>
            </OptionalViewport>
          </Stack>
        </Grid>
        <OptionalViewport
          viewport={'d'}
          initIsMobile={isMobile}
          condition={ctaIsTop && align === 'left'}
        >
          <Grid size={'auto'} sx={{ alignSelf: 'end' }}>
            <RichCtaButton
              cta={cta}
              track={{ position: getRichTextSummary(title) }}
            />
          </Grid>
        </OptionalViewport>
        <OptionalViewport
          viewport={'d'}
          initIsMobile={isMobile}
          condition={isSubtitleRight}
        >
          <Grid size={3} sx={{ alignSelf: 'end' }}>
            <RichText
              withTypography
              variant={'h6'}
              component={'p'}
              color={'textSecondary'}
            >
              {subtitle}
            </RichText>
          </Grid>
        </OptionalViewport>
      </Grid>
    </Stack>
  );
};

export default Title;
